// Atajos de teclado del editor de flujo, configurables por quien lo usa.
//
// Se guardan en localStorage y no en la base de datos a proposito: son una
// preferencia de cada persona y de cada navegador, como la escala de la
// cronologia. No merece una tabla ni un viaje al servidor.

const CLAVE = "alm.flujo.atajos";

// Lo que se puede atajar. `accion` es lo que entiende FlowView.
export const ACCIONES = [
  { id: "nodo:tarea",       label: "Nuevo paso",        icon: "kanban-fill",   defecto: "p" },
  { id: "nodo:inicio",      label: "Nuevo inicio",      icon: "play-fill",     defecto: "i" },
  { id: "nodo:fin",         label: "Nuevo fin",         icon: "flag",          defecto: "f" },
  { id: "nodo:condicional", label: "Nuevo condicional", icon: "signpost-split", defecto: "c" },
  { id: "nodo:hito",        label: "Nuevo hito",        icon: "diamond-fill",  defecto: "h" },
  // Conectar: nace del paso seleccionado hacia la derecha. Son dos acciones y
  // no una con modificador porque la rama opcional se usa tanto como la
  // principal y esconderla tras una tecla muerta la haria invisible.
  { id: "conectar:normal",  label: "Conectar",          icon: "arrow-right-short", defecto: "l" },
  { id: "conectar:opcional", label: "Conectar (rama opcional)", icon: "dash-lg",  defecto: "o" },
  { id: "borrar",           label: "Eliminar lo seleccionado", icon: "trash3", defecto: "Delete" },
  { id: "encajar",          label: "Ver todo",          icon: "arrows-fullscreen", defecto: "0" },
  { id: "organizar",        label: "Reorganizar",       icon: "diagram-3",     defecto: "r" },
];

// Atajos que NO se pueden reasignar porque son convenciones del sistema y
// romperlas confunde mas de lo que ayuda.
export const FIJOS = [
  { teclas: "Ctrl+Z",       label: "Deshacer" },
  { teclas: "Ctrl+Shift+Z", label: "Rehacer" },
  { teclas: "Escape",       label: "Cancelar o deseleccionar" },
  { teclas: "Espacio",      label: "Mover el lienzo mientras se mantiene" },
  { teclas: "+  /  -",      label: "Acercar y alejar" },
  { teclas: "Flechas",      label: "Mover la seleccion (Shift = 1 px)" },
];

export const porDefecto = () =>
  Object.fromEntries(ACCIONES.map(a => [a.id, a.defecto]));

export function leer() {
  try {
    const guardado = JSON.parse(localStorage.getItem(CLAVE) || "{}");
    // Se parte siempre de los valores por defecto: si mañana se añade una
    // accion nueva, quien tenga atajos guardados la recibe igual en vez de
    // quedarse sin ella.
    return { ...porDefecto(), ...guardado };
  } catch {
    return porDefecto();
  }
}

export function guardar(mapa) {
  try { localStorage.setItem(CLAVE, JSON.stringify(mapa)); } catch { /* privado */ }
}

// Nombre legible de una tecla, y a la vez la forma canonica con la que se
// compara: lo que se guarda es esto, no el keyCode.
export function nombreTecla(e) {
  if (e.key === " ") return "Espacio";
  if (e.key === "Delete") return "Delete";
  if (e.key === "Backspace") return "Backspace";
  if (e.key === "Escape") return "Escape";
  if (e.key === "Enter") return "Enter";
  if (e.key === "Tab") return "Tab";
  if (e.key.startsWith("Arrow")) return e.key;
  if (e.key.length === 1) return e.key.toLowerCase();
  return e.key;
}

export const etiquetaTecla = k => ({
  Delete: "Supr", Backspace: "Retroceso", Escape: "Esc", Espacio: "Espacio",
  ArrowUp: "↑", ArrowDown: "↓", ArrowLeft: "←", ArrowRight: "→",
}[k] || (k?.length === 1 ? k.toUpperCase() : k));

// Que accion corresponde a una tecla. Devuelve null si no hay ninguna, o si la
// tecla lleva modificadores: los atajos de una sola letra no deben dispararse
// con Ctrl o Alt, que son de otros atajos del navegador.
export function accionDe(mapa, e) {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const k = nombreTecla(e);
  for (const [accion, tecla] of Object.entries(mapa || {}))
    if (tecla && tecla === k) return accion;
  return null;
}

// Una tecla no puede estar en dos acciones a la vez.
export function conflictoDe(mapa, accionId, tecla) {
  for (const [id, k] of Object.entries(mapa || {}))
    if (id !== accionId && k === tecla) return id;
  return null;
}

// Teclas que no se dejan asignar: o las usa el propio editor para otra cosa, o
// son de escritura y bloquearlas rompe los campos del panel.
export const RESERVADAS = new Set(["Escape", "Enter", "Tab", "Espacio",
  "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "+", "-", "="]);
